/*
 * ozel-infinite-scroll.js
 * Bottomless (infinite) scrolling for the Ozel custom collection grid (PLP).
 *
 * Behaviour only -- does not alter any product text, links, or captions.
 * Targets the custom markup in sections/collection-grid.liquid:
 *   - grid list : .oz-coll-grid__list  (appends <li class="oz-coll-grid__cell">)
 *   - pager     : .oz-coll-grid__pager [data-next-url]  (used only to detect that
 *                 a further page exists on first load)
 *
 * The next-page URL is rebuilt from the CURRENT address each time, so:
 *   - preview_theme_id is preserved (preview keeps rendering the draft theme),
 *   - active filters + sort (filter.* / sort_by query params) are preserved.
 * Loading stops when a fetched page returns zero product cells.
 * The manual pager is hidden while JS drives loading; restored on fetch error.
 * Both an IntersectionObserver sentinel and a throttled scroll listener drive
 * loading, so it still advances if one mechanism misbehaves.
 */
(function () {
  'use strict';

  if (!window.DOMParser || !window.URLSearchParams) return;

  var GRID = '.oz-coll-grid__list';
  var PAGER = '.oz-coll-grid__pager';
  var CELL = '.oz-coll-grid__cell';
  var MAX_PAGES = 200; // safety stop against any runaway loop

  function buildUrl(page) {
    var params = new URLSearchParams(window.location.search);
    params.set('page', String(page));
    var qs = params.toString();
    return window.location.pathname + (qs ? '?' + qs : '');
  }

  function init(section) {
    if (!section) return;

    var grid = section.querySelector(GRID);
    var pager = section.querySelector(PAGER);

    // No grid, or only a single page of results (no pager) - nothing to do.
    if (!grid || !pager) return;

    // Guard against double-initialising the same pager instance.
    if (pager.dataset.ozelInfinite) return;

    // Only engage if the server says there is a page beyond this one.
    var hasNext = (pager.getAttribute('data-next-url') || '').trim() !== '';
    if (!hasNext) {
      pager.dataset.ozelInfinite = 'done';
      return;
    }
    pager.dataset.ozelInfinite = 'on';

    var params = new URLSearchParams(window.location.search);
    var currentPage = parseInt(params.get('page'), 10) || 1;

    // Hide the numeric pager (kept in DOM as a no-JS / error fallback).
    pager.style.display = 'none';

    var sentinel = document.createElement('div');
    sentinel.className = 'ozel-infinite-sentinel';
    sentinel.setAttribute('aria-hidden', 'true');

    var status = document.createElement('div');
    status.className = 'ozel-infinite-status';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');

    pager.parentNode.insertBefore(status, pager);
    pager.parentNode.insertBefore(sentinel, status);

    var loading = false;
    var done = false;
    var observer = null;

    function stop() {
      done = true;
      if (observer) observer.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (sentinel.parentNode) sentinel.parentNode.removeChild(sentinel);
      if (status.parentNode) status.parentNode.removeChild(status);
    }

    function loadNext() {
      if (loading || done) return;
      if (currentPage + 1 > MAX_PAGES) {
        stop();
        return;
      }
      loading = true;
      status.classList.add('is-loading');

      var url = buildUrl(currentPage + 1);

      fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(function (res) {
          if (!res.ok) throw new Error('Request failed: ' + res.status);
          return res.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var newGrid = doc.querySelector(GRID);
          var cells = newGrid ? newGrid.querySelectorAll(CELL) : [];

          if (cells.length === 0) {
            loading = false;
            status.classList.remove('is-loading');
            stop();
            return;
          }

          var frag = document.createDocumentFragment();
          for (var i = 0; i < cells.length; i++) {
            frag.appendChild(document.importNode(cells[i], true));
          }
          grid.appendChild(frag);

          currentPage += 1;
          loading = false;
          status.classList.remove('is-loading');

          // If the fetched page itself reports no further pages, stop.
          var newPager = doc.querySelector(PAGER);
          if (newPager && (newPager.getAttribute('data-next-url') || '').trim() === '') {
            stop();
            return;
          }

          // Page may still be short enough that the sentinel is in view.
          maybeLoad();
        })
        .catch(function () {
          loading = false;
          status.classList.remove('is-loading');
          done = true;
          if (observer) observer.disconnect();
          window.removeEventListener('scroll', onScroll);
          window.removeEventListener('resize', onScroll);
          if (sentinel.parentNode) sentinel.parentNode.removeChild(sentinel);
          if (status.parentNode) status.parentNode.removeChild(status);
          // Restore the manual pager so browsing still works.
          pager.style.display = '';
        });
    }

    function nearBottom() {
      var rect = sentinel.getBoundingClientRect();
      var vh = window.innerHeight || document.documentElement.clientHeight;
      return rect.top <= vh + 800;
    }

    function maybeLoad() {
      if (!done && !loading && nearBottom()) loadNext();
    }

    // Primary trigger: IntersectionObserver on the sentinel.
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        function (entries) {
          for (var i = 0; i < entries.length; i++) {
            if (entries[i].isIntersecting) {
              loadNext();
              break;
            }
          }
        },
        { rootMargin: '800px 0px' }
      );
      observer.observe(sentinel);
    }

    // Fallback trigger: throttled scroll / resize.
    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        ticking = false;
        maybeLoad();
      });
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });

    // Kick once in case the first page is short enough to already show the end.
    maybeLoad();
  }

  function run() {
    var section = document.querySelector('.oz-coll-grid');
    if (section) init(section);
  }

  function start() {
    run();

    // Re-initialise if facets / sort replace the grid section wholesale.
    var debounce = null;
    var reinit = new MutationObserver(function () {
      if (debounce) return;
      debounce = setTimeout(function () {
        debounce = null;
        run();
      }, 150);
    });
    reinit.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
